import { useState, useRef, useCallback } from 'react';

export function useScreenShare({ onStart, onStop } = {}) {
	const [isSharing, setIsSharing] = useState(false);
	const [error, setError] = useState(null);
	const screenStreamRef = useRef(null);
	const originalVideoTrackRef = useRef(null);

	const stopScreenShare = useCallback(async (peerConnections = {}, originalStream = null, { stopTracks = true } = {}) => {
		if (screenStreamRef.current) {
			if (stopTracks) screenStreamRef.current.getTracks().forEach((t) => t.stop());
			screenStreamRef.current = null;
		}
		setIsSharing(false);

		// Restore original video track if available
		const originalVideoTrack = originalStream?.getVideoTracks?.()[0] || originalVideoTrackRef.current;
		originalVideoTrackRef.current = null;
		if (originalVideoTrack) {
			Object.values(peerConnections).forEach((pc) => {
				if (!pc || pc.connectionState === 'closed') return;
				const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
				if (sender) sender.replaceTrack(originalVideoTrack).catch(() => {});
			});
		}

		if (typeof onStop === 'function') onStop();
	}, [onStop]);

	const startScreenShare = useCallback(async (peerConnections = {}) => {
		setError(null);
		try {
			if (!navigator.mediaDevices?.getDisplayMedia) {
				throw new Error('Screen sharing is available only in supported browsers over HTTPS or localhost.');
			}

			const screenStream = await navigator.mediaDevices.getDisplayMedia({
				video: { cursor: 'always' },
				audio: false,
			});

			const videoTrack = screenStream.getVideoTracks()[0];
			if (!videoTrack) {
				screenStream.getTracks().forEach((track) => track.stop());
				throw new Error('No screen video track was selected.');
			}

			// Replace video track in all active peer connections
			const replacePromises = Object.values(peerConnections).map((pc) => {
				if (!pc || pc.connectionState === 'closed') return;
				const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
				if (!sender) return;
				if (!originalVideoTrackRef.current) originalVideoTrackRef.current = sender.track;
				return sender.replaceTrack(videoTrack);
			});
			await Promise.all(replacePromises.filter(Boolean));

			screenStreamRef.current?.getTracks().forEach((track) => track.stop());
			screenStreamRef.current = screenStream;
			setIsSharing(true);

			// Stop sharing when user clicks browser's "Stop sharing" button
			videoTrack.addEventListener('ended', () => stopScreenShare(peerConnections, null, { stopTracks: false }), { once: true });

			if (typeof onStart === 'function') onStart(screenStream);
			return screenStream;
		} catch (err) {
			if (err.name !== 'NotAllowedError') {
				console.error('useScreenShare: getDisplayMedia error:', err);
				setError(err.message || 'Screen share failed');
			}
			return null;
		}
	}, [onStart, stopScreenShare]);

	return {
		isSharing,
		error,
		screenStreamRef,
		startScreenShare,
		stopScreenShare,
	};
}
